import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type Quantity } from '@luminovo/api-contracts';
import { CostSummaryDTO } from '@luminovo/api-contracts';
import { extractAmountFromMonetaryValue, isPresent } from '@luminovo/commons';
import { colorSystem, Flexbox, Text } from '@luminovo/design-system';
import { formatDecimal, formatMonetaryValue } from '@luminovo/frontend-utils';

import { formatQuantity } from '../../formatters';

interface CompareLineItemCostBreakdownProps {
    costSummary: CostSummaryDTO;
    aggregatedQuantity: Quantity;
    scrapQuantity?: { amount: number } | null;
}

function CostItemRow({
    label,
    value,
    valuePrefix,
}: {
    label: string;
    value: string;
    valuePrefix?: string;
}): JSX.Element {
    return (
        <Flexbox
            sx={{
                justifyContent: 'space-between',
                paddingBottom: '4px',
            }}
        >
            <Text variant="body-small" color={colorSystem.neutral[7]}>
                {label}
            </Text>
            <Flexbox
                gap="4px"
                sx={{
                    alignItems: 'center',
                }}
            >
                {valuePrefix && (
                    <Text variant="body-small" color={colorSystem.neutral[6]}>
                        {valuePrefix}
                    </Text>
                )}
                <Text variant="body-small" color={colorSystem.neutral[9]} tabularNums>
                    {value}
                </Text>
            </Flexbox>
        </Flexbox>
    );
}

/** Cost summary pinned to the bottom of the compare card. */
export function CompareLineItemCostBreakdown({
    costSummary,
    aggregatedQuantity,
    scrapQuantity,
}: CompareLineItemCostBreakdownProps): JSX.Element {
    const totalPrice = costSummary.total_price?.preferred;
    const preferredUnitPrice = costSummary.unit_price?.preferred;
    const unitTcoCost = costSummary.tco_costs?.unit.cost?.preferred;
    // The backend sends a zero TCO cost for offers that no TCO rule applies to.
    const hasUnitTcoCost = isPresent(unitTcoCost) && extractAmountFromMonetaryValue(unitTcoCost) !== 0;
    const totalOneTimeCost = costSummary.one_time_costs;
    const hasScrap = scrapQuantity && scrapQuantity.amount > 0;

    return (
        <Flexbox
            sx={{
                flexDirection: 'column',
            }}
        >
            <Flexbox
                sx={{
                    flexDirection: 'column',
                    padding: '8px',
                    gap: '4px',
                    backgroundColor: colorSystem.neutral[0],
                }}
            >
                <CostItemRow
                    label={t`Unit price`}
                    value={preferredUnitPrice ? formatMonetaryValue(preferredUnitPrice, 'unit-price') : '-'}
                />

                {/* Unit TCO cost - part of the total price, so it has to be listed for the rows to add up */}
                {hasUnitTcoCost && (
                    <CostItemRow
                        label={t`Unit TCO cost`}
                        value={formatMonetaryValue(unitTcoCost, 'unit-price')}
                        valuePrefix="+"
                    />
                )}

                <CostItemRow label={t`Quantity`} value={formatQuantity(aggregatedQuantity)} valuePrefix="x" />

                {hasScrap && (
                    <CostItemRow label={t`Scrap`} value={formatDecimal(scrapQuantity.amount)} valuePrefix="+" />
                )}

                {totalOneTimeCost && (
                    <CostItemRow
                        label={t`One-time costs`}
                        value={formatMonetaryValue(totalOneTimeCost.preferred, 'default')}
                        valuePrefix="+"
                    />
                )}
            </Flexbox>
            <Flexbox
                sx={{
                    justifyContent: 'space-between',
                    padding: '8px',
                    backgroundColor: colorSystem.primary[1],
                    borderTop: `1px solid ${colorSystem.primary[2]}`,
                }}
            >
                <Text variant="body-semibold" color={colorSystem.primary[7]}>
                    <Trans>Total price</Trans>
                </Text>
                <Text variant="body-semibold" color={colorSystem.primary[7]} tabularNums>
                    {formatMonetaryValue(totalPrice)}
                </Text>
            </Flexbox>
        </Flexbox>
    );
}
